import type { ErrorInfo } from 'react'
import { datadogLogs } from '@datadog/browser-logs'
import { datadogRum } from '@datadog/browser-rum'
import { canUseWindow } from '@library/environment'

export enum CODEOWNERS {
  UNKNOWN = 'unknown', // Only for very high level errors - explicit ownership is favoured
  TURNIPS = 'turnips',
  ROCKETS = 'rockets',
  TOMATOES = 'tomatoes',
  BEETROOTS = 'beetroots',
}

export enum BROWSER_ERROR_KEYS {
  'TEST_KEY' = 'BROWSER_ERROR_000',
  'TEST_KEY_FOR_ERROR_BOUNDARY' = 'BROWSER_ERROR_001',
  'FULL_PAGE_ERROR' = 'BROWSER_ERROR_002',
  'CHECKOUT_ORDER_ERROR' = 'BROWSER_ERROR_003',
  'POSTCODE_WIZARD_ERROR' = 'BROWSER_ERROR_004',
  'PAYPAL_ERROR' = 'BROWSER_ERROR_005',
  'VALIDATE_EMAIL_ERROR' = 'BROWSER_ERROR_006',
  'UNEXPECTED_MENU_PAGE_ERROR' = 'BROWSER_ERROR_007',
  'MENU_AUTHENTICATION_CHECK_ERROR' = 'BROWSER_ERROR_008',
  'MENU_LOAD_ERROR' = 'BROWSER_ERROR_009',
  'INVALID_ORDER_ACCESS_MENU_ERROR' = 'BROWSER_ERROR_010',
  'INVALID_SIGNUP_POST_DATA' = 'BROWSER_ERROR_011',
  'INVALID_SIGNUP_RESPONSE' = 'BROWSER_ERROR_012',
  'REACTIVATE_SUBSCRIPTION_ERROR' = 'BROWSER_ERROR_013',
  'SUBSCRIPTION_TO_DELIVERY_DAYS_ERROR' = 'BROWSER_ERROR_014',
  'UPDATE_SUBSCRIPTION_ERROR' = 'BROWSER_ERROR_015',
  'UNEXPECTED_ORDER_CONFIRMATION_PAGE_ERROR' = 'BROWSER_ERROR_016',
  'ORDER_CONFIRMATION_AUTHENTICATION_CHECK_ERROR' = 'BROWSER_ERROR_017',
  'UNEXPECTED_DELIVERY_DETAILS_PAGE_ERROR' = 'BROWSER_ERROR_018',
  'PROMO_CODE_APPLY_ERROR' = 'BROWSER_ERROR_019',
  'FAILED_TO_LOAD_ORDERS' = 'BROWSER_ERROR_020',
  'FAILED_TO_LOAD_PROMOS' = 'BROWSER_ERROR_021',
  'FAILED_TO_LOAD_BOXES' = 'BROWSER_ERROR_022',
  'FAILED_TO_LOAD_MENU' = 'BROWSER_ERROR_023',
  'FAILED_TO_LOAD_USER' = 'BROWSER_ERROR_024',
  'CHECKOUT_PREVIEW_BASKET_ERROR' = 'BROWSER_ERROR_025',
  'CHECKOUT_PREVIEW_REQUEST_ERROR' = 'BROWSER_ERROR_026',
  'CMS_BLOCK_RENDER_ERROR' = 'BROWSER_ERROR_027',
  'CMS_BLOCK_VALIDATION_ERROR' = 'BROWSER_ERROR_028',
}

type BrowserErrorRequiredKeys = {
  description: string
  owner: CODEOWNERS
  detail: Record<string, unknown>
  expected?: boolean
}

function createBrowserErrors<T extends Record<BROWSER_ERROR_KEYS, BrowserErrorRequiredKeys>>(
  errors: T,
): T {
  return errors
}

export const BrowserErrors = createBrowserErrors({
  // Testing only
  [BROWSER_ERROR_KEYS.TEST_KEY]: {
    description: 'Testing only',
    owner: CODEOWNERS.BEETROOTS,
    detail: {},
  },
  // Testing only
  [BROWSER_ERROR_KEYS.TEST_KEY_FOR_ERROR_BOUNDARY]: {
    description: 'Testing only',
    owner: CODEOWNERS.BEETROOTS,
    detail: {} as { errorInfo: ErrorInfo },
  },
  [BROWSER_ERROR_KEYS.FULL_PAGE_ERROR]: {
    description: 'User saw an error page',
    owner: CODEOWNERS.UNKNOWN,
    detail: {} as { status: 403 | 404 | 500; cause: unknown },
  },
  [BROWSER_ERROR_KEYS.CHECKOUT_ORDER_ERROR]: {
    description: 'User saw checkout error modal',
    owner: CODEOWNERS.BEETROOTS,
    detail: {} as { isAuthenticated: boolean | undefined; cause: unknown },
  },
  [BROWSER_ERROR_KEYS.POSTCODE_WIZARD_ERROR]: {
    description: 'User saw error when submitting postcode',
    owner: CODEOWNERS.BEETROOTS,
    detail: {} as { message: string; footnote: string; cause: unknown },
  },
  [BROWSER_ERROR_KEYS.PAYPAL_ERROR]: {
    description: 'User saw error when entering checkout',
    owner: CODEOWNERS.BEETROOTS,
    detail: {} as { message: string; cause: unknown },
  },
  [BROWSER_ERROR_KEYS.VALIDATE_EMAIL_ERROR]: {
    description: 'User saw error when creating user account',
    owner: CODEOWNERS.BEETROOTS,
    detail: {} as { message: string; cause: unknown },
  },
  [BROWSER_ERROR_KEYS.UNEXPECTED_MENU_PAGE_ERROR]: {
    description: 'User saw error when accessing the menu page',
    owner: CODEOWNERS.TURNIPS,
    detail: {} as { errorInfo: ErrorInfo },
    expected: false,
  },
  [BROWSER_ERROR_KEYS.MENU_AUTHENTICATION_CHECK_ERROR]: {
    description: 'User saw error when accessing the menu page due to an authentication check error',
    owner: CODEOWNERS.TURNIPS,
    detail: {},
    expected: false,
  },
  [BROWSER_ERROR_KEYS.MENU_LOAD_ERROR]: {
    description: 'User saw a menu failed to load error page',
    owner: CODEOWNERS.TURNIPS,
    detail: {} as { online: boolean },
    expected: true,
  },
  [BROWSER_ERROR_KEYS.INVALID_ORDER_ACCESS_MENU_ERROR]: {
    description: 'Non-logged in user attempting to access order via the menu',
    owner: CODEOWNERS.TURNIPS,
    detail: {} as { orderId: string },
    expected: true,
  },
  [BROWSER_ERROR_KEYS.INVALID_SIGNUP_POST_DATA]: {
    description: 'Invalid data was used to sign up a new user',
    owner: CODEOWNERS.BEETROOTS,
    detail: {} as { message: string; cause: unknown; validationErrors: string[] },
  },
  [BROWSER_ERROR_KEYS.INVALID_SIGNUP_RESPONSE]: {
    description: 'Invalid response was received when signing up a new user',
    owner: CODEOWNERS.BEETROOTS,
    detail: {} as { message: string; cause: unknown },
  },
  [BROWSER_ERROR_KEYS.REACTIVATE_SUBSCRIPTION_ERROR]: {
    description: 'User saw error when reactivating their subscription',
    owner: CODEOWNERS.ROCKETS,
    detail: {} as { error: { status: number; errors: string[] } },
  },
  [BROWSER_ERROR_KEYS.SUBSCRIPTION_TO_DELIVERY_DAYS_ERROR]: {
    description: 'User saw error when updating their delivery date in subscription',
    owner: CODEOWNERS.ROCKETS,
    detail: {} as { error: unknown },
  },
  [BROWSER_ERROR_KEYS.UPDATE_SUBSCRIPTION_ERROR]: {
    description: 'User saw error when updating their delivery date in subscription',
    owner: CODEOWNERS.ROCKETS,
    detail: {} as { response: unknown },
  },
  [BROWSER_ERROR_KEYS.UNEXPECTED_ORDER_CONFIRMATION_PAGE_ERROR]: {
    description: 'User saw error when accessing the order confirmation page',
    owner: CODEOWNERS.TURNIPS,
    detail: {} as { errorInfo: ErrorInfo },
    expected: false,
  },
  [BROWSER_ERROR_KEYS.ORDER_CONFIRMATION_AUTHENTICATION_CHECK_ERROR]: {
    description:
      'User saw error when accessing the order confirmation page due to an authentication check error',
    owner: CODEOWNERS.TURNIPS,
    detail: {},
    expected: false,
  },
  [BROWSER_ERROR_KEYS.UNEXPECTED_DELIVERY_DETAILS_PAGE_ERROR]: {
    description: 'User saw error when accessing the delivery details page',
    owner: CODEOWNERS.TURNIPS,
    detail: {} as { errorInfo: ErrorInfo },
    expected: false,
  },
  [BROWSER_ERROR_KEYS.PROMO_CODE_APPLY_ERROR]: {
    description: 'Error applying promo code',
    owner: CODEOWNERS.BEETROOTS,
    detail: {} as { cause: unknown },
  },
  [BROWSER_ERROR_KEYS.FAILED_TO_LOAD_ORDERS]: {
    description: 'Request to load orders failed',
    owner: CODEOWNERS.TURNIPS,
    detail: {} as {
      source: string
      actionType: string
      orderType: string
      forceRefresh: boolean
      errorTag: string
      error: unknown
    },
  },
  [BROWSER_ERROR_KEYS.FAILED_TO_LOAD_PROMOS]: {
    description: 'Request to load promos failed',
    owner: CODEOWNERS.TOMATOES,
    detail: {} as { page: string; error: unknown },
  },
  [BROWSER_ERROR_KEYS.FAILED_TO_LOAD_BOXES]: {
    description: 'Request to load boxes failed',
    owner: CODEOWNERS.TURNIPS,
    detail: {} as {
      source: string
      actionType: string
      stage: string
      sessionId: string | undefined
      errorMessage: string
    },
  },
  [BROWSER_ERROR_KEYS.FAILED_TO_LOAD_MENU]: {
    description: 'Request to load menu failed',
    owner: CODEOWNERS.TURNIPS,
    detail: {} as {
      source: string
      actionType: string
      stage: string
      deliveryDate: string
      orderId?: string
      numPortions?: number
      isAuthenticated?: boolean
      newMenuNavVariationKey?: string
      isPreviewMode?: boolean
      errorMessage: string
      errorTag?: string
    },
  },
  [BROWSER_ERROR_KEYS.FAILED_TO_LOAD_USER]: {
    description: 'Request to load user failed',
    owner: CODEOWNERS.TURNIPS,
    detail: {} as {
      source: string
      isAdmin: boolean
      hasRefreshCookie: boolean
      rememberMe: boolean
      isAuthenticated: boolean
      accessToken: string
      id: string
      roles: string[]
      error: unknown
    },
  },
  [BROWSER_ERROR_KEYS.CHECKOUT_PREVIEW_BASKET_ERROR]: {
    description: 'Checkout preview order was blocked by an expired or incomplete basket',
    owner: CODEOWNERS.BEETROOTS,
    detail: {} as {
      isAuthenticated: boolean
      path: string | undefined
      deliveryDayId: string | undefined
      deliverySlotId: string | undefined
      recipeChoiceCount: number
      reason: 'basket-expired' | 'insufficient-recipes'
    },
    expected: true,
  },
  [BROWSER_ERROR_KEYS.CHECKOUT_PREVIEW_REQUEST_ERROR]: {
    description: 'Checkout preview order request failed',
    owner: CODEOWNERS.BEETROOTS,
    detail: {} as {
      isAuthenticated: boolean
      path: string | undefined
      deliveryDayId: string | undefined
      deliverySlotId: string | undefined
      recipeChoiceCount: number
      code: string | undefined
      message: string | undefined
    },
    expected: false,
  },
  [BROWSER_ERROR_KEYS.CMS_BLOCK_RENDER_ERROR]: {
    description: 'A CMS block threw during render; SafeBlock caught it and rendered null',
    owner: CODEOWNERS.BEETROOTS,
    detail: {} as { errorInfo: ErrorInfo; blockKey: string },
  },
  [BROWSER_ERROR_KEYS.CMS_BLOCK_VALIDATION_ERROR]: {
    description: 'A CMS block payload failed Zod validation; PageRenderer skipped the section',
    owner: CODEOWNERS.BEETROOTS,
    detail: {} as {
      blockUid: string
      sectionIndex: number
      issues: Array<{ path: Array<string | number>; message: string; code: string }>
    },
  },
})

/**
 * Shared utility to track errors in datadog RUM
 * @param {BROWSER_ERROR_KEYS} key - Error Identifier
 * @param {typeof BrowserErrors[BROWSER_ERROR_KEYS]['detail']} detail - Corresponding details properties for your error key as defined in BrowserErrors
 * @param {Error} [error] - thrown error at runtime to be unminified by DataDog
 * @returns void
 */
export function instrumentBrowserError<ErrorKey extends BROWSER_ERROR_KEYS>(
  key: ErrorKey,
  detail: typeof BrowserErrors[ErrorKey]['detail'],
  error?: Error,
) {
  if (!canUseWindow()) {
    return
  }

  try {
    const { detail: _detail, ...staticErrorInfo } = BrowserErrors[key]

    datadogRum.addError(error || new Error(staticErrorInfo.description), {
      ...staticErrorInfo,
      ...detail,
      key,
    })
  } catch (unsafeRUMError) {
    const RUMError = unsafeRUMError instanceof Error ? unsafeRUMError : undefined
    datadogLogs.logger.error('Unable to instrument browser error', {}, RUMError)
  }
}
